import React from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import { TruckIcon, ArrowUturnLeftIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useGetFulfillmentGroupTrackingQuery } from '../../../../redux/apis/endpoints/deliveryApi';

const TrackingModal = ({ 
  isOpen, 
  onClose, 
  entity 
}) => {
  const fulfillmentGroupId = entity?.id || entity?.fulfillmentGroupId;
  
  const { 
    data: trackingData, 
    isLoading, 
    error 
  } = useGetFulfillmentGroupTrackingQuery(fulfillmentGroupId, {
    skip: !isOpen || !fulfillmentGroupId
  });

  const formatTrackingStatus = (status) => {
    const statusMap = {
      'pre_transit': 'Label Created',
      'transit': 'In Transit',
      'delivered': 'Delivered',
      'returned': 'Returned',
      'failure': 'Delivery Failed',
      'cancelled': 'Cancelled',
      'error': 'Error'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pre_transit': 'text-yellow-600 bg-yellow-100',
      'transit': 'text-blue-600 bg-blue-100',
      'delivered': 'text-green-600 bg-green-100',
      'returned': 'text-purple-600 bg-purple-100',
      'failure': 'text-red-600 bg-red-100',
      'cancelled': 'text-gray-600 bg-gray-100',
      'error': 'text-red-600 bg-red-100'
    };
    return colorMap[status] || 'text-gray-600 bg-gray-100';
  };

  const TrackingCard = ({ tracking, type }) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
      {tracking.trackingNumber && (
          <div>
            <span className="text-sm font-medium text-gray-700">Tracking Number:</span>
            <span className="ml-2 text-sm text-gray-900 font-mono">
              {tracking.trackingNumber}
            </span>
          </div>
        )}
        {tracking.status && (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tracking.status)}`}>
            {formatTrackingStatus(tracking.status)}
          </span>
        )}
      </div>

      <div className="space-y-2">
       

        {tracking.carrier && (
          <div>
            <span className="text-sm font-medium text-gray-700">Carrier:</span>
            <span className="ml-2 text-sm text-gray-900">
              {tracking.carrier.toUpperCase()}
            </span>
          </div>
        )}

        {tracking.estimatedDelivery && (
          <div>
            <span className="text-sm font-medium text-gray-700">Estimated Delivery:</span>
            <span className="ml-2 text-sm text-gray-900">
              {new Date(tracking.estimatedDelivery).toLocaleDateString()}
            </span>
          </div>
        )}

        {tracking.trackingUrl && (
          <div className="pt-2">
            <a
              href={tracking.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-brand-teal hover:text-brand-teal-dark"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
              Track on {tracking.carrier?.toUpperCase() || 'Carrier'} Website
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tracking Information"
      size="lg"
    >
      <div className="py-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}

        {error && (
          <div className="text-center py-8">
            <p className="text-red-600">Failed to load tracking information.</p>
            <p className="text-sm text-gray-500 mt-1">
              Please try again later or contact support.
            </p>
          </div>
        )}

        {trackingData && !isLoading && (
          <div className="space-y-6">
            {/* Outbound Tracking */}
            {trackingData.outboundTracking && trackingData.outboundTracking.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Delivery Tracking
                </h3>
                <div className="space-y-4">
                  {trackingData.outboundTracking.map((tracking, index) => (
                    <TrackingCard 
                      key={`outbound-${index}`} 
                      tracking={tracking} 
                      type="outbound" 
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Return Tracking */}
            {trackingData.returnTracking && trackingData.returnTracking.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Return Tracking
                </h3>
                <div className="space-y-4">
                  {trackingData.returnTracking.map((tracking, index) => (
                    <TrackingCard 
                      key={`return-${index}`} 
                      tracking={tracking} 
                      type="return" 
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No Tracking Available */}
            {(!trackingData.outboundTracking || trackingData.outboundTracking.length === 0) &&
             (!trackingData.returnTracking || trackingData.returnTracking.length === 0) && (
              <div className="text-center py-8">
                <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No tracking information available yet.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Tracking details will appear here once shipping labels are created.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button
            onClick={onClose}
            variant="secondary"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TrackingModal; 